import {
  Button,
  FileButton,
  Image,
  Menu,
  Modal,
  Select,
  TextInput,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { Copy, MoreHorizontal, Upload } from "lucide-react";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { CSVLink } from "react-csv";
import { Link } from "react-router-dom";
import {
  deleteUser,
  generatePaymentLink,
  getAllSubscribtions,
  getUserNotication,
  updateUserImage,
  userSubHistory,
} from "../../utils/api";
import DataTable from "../common/DataTable";

const UsersTable = ({
  users,
  loading,
  getAllUserData,
  type,
  selectedUsers,
  setSelectedUsers,
  onEnableUsers,
  onDisableUsers,
  current,
  setCurrent,
  limit,
  setLimit,
  currentPage,
}) => {
  const [loading2, setLoading2] = useState(false);
  const [loading3, setLoading3] = useState(false);
  const [modal3, setModal3] = useState(false);
  const [userNotifications, setUserNotifications] = useState([]);
  const [allSubscriptions, setAllSubscriptions] = useState([]);

  const showSuccess = (msg) => notifications.show({ message: msg, color: "green" });
  const showError = (msg) => notifications.show({ message: msg, color: "red" });

  const getUserNoticationData = async (id) => {
    setLoading2(true);
    const [success, response] = await getUserNotication(id);
    if (success) setUserNotifications(response);
    setLoading2(false);
    setModal3(true);
  };

  const getAllSubsHandler = async () => {
    const [success, data] = await getAllSubscribtions();
    if (success) setAllSubscriptions(data);
  };

  React.useEffect(() => { getAllSubsHandler(); }, []);

  const rowSelection = selectedUsers && setSelectedUsers ? {
    selectedRowKeys: selectedUsers,
    onChange: (keys) => setSelectedUsers(keys),
  } : undefined;

  const getActionColumn = () => ({
    title: "Actions", dataIndex: "Actions", key: "Actions",
    render: (text, record) => (
      <div className="col2">
        <Menu shadow="md" width={220} position="bottom-end">
          <Menu.Target>
            <Button variant="default" size="xs"><MoreHorizontal size={16} /></Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item onClick={async () => {
              const [success, response] = await userSubHistory(record?._id);
              if (success) {
                modals.open({
                  title: "Subscriptions History", size: "lg",
                  children: (
                    <div>{response?.[0]?.history?.map((item, i) => <div key={i} className="p-2 border-b border-zinc-800">{item}</div>)}</div>
                  ),
                });
              } else { showError("Something went wrong"); }
            }}>Subscriptions History</Menu.Item>
            {(type === "enabled" || type === "subscriptions") && (
              <Menu.Item>
                <SubscriptionLink item={record} showSuccess={showSuccess} showError={showError} subscription={allSubscriptions || []} />
              </Menu.Item>
            )}
            <Menu.Item loading={loading2} onClick={() => getUserNoticationData(record._id)}>Notification</Menu.Item>
            <Menu.Item>
              <UpdatePhotoButton record={record} getAllUserData={getAllUserData} current={current} limit={limit}
                showSuccess={showSuccess} showError={showError} />
            </Menu.Item>
            <Menu.Item><Link to={`/users/edit/${record._id}?page=${currentPage}`}>Edit</Link></Menu.Item>
            <Menu.Item color="red" onClick={() => {
              modals.openConfirmModal({
                title: "Are you sure delete this user?",
                children: <p>Please Confirm</p>,
                labels: { confirm: "Delete", cancel: "Cancel" },
                confirmProps: { color: "red" },
                onConfirm: async () => {
                  const [success, response] = await deleteUser(record._id);
                  if (success) { showSuccess("User deleted successfully"); if (getAllUserData && current && limit) getAllUserData(current, limit); }
                  else { showError(response); }
                },
              });
            }}>Delete</Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </div>
    ),
  });

  return (
    <div className="flex flex-col gap-4">
      {type !== "subscriptions" && (
        <div className="flex justify-between items-center mt-3">
          <CSVLink target="_blank"
            data={(users?.data || []).map((item) => ({
              name: item.name, email: item.email, phoneNumber: item.phoneNumber,
              createdAt: new Date(item.createdAt).toLocaleDateString(),
            }))}
            filename={`${type}-users-${new Date().toLocaleDateString()}.csv`}>
            <Button mr={8}>Export as CSV</Button>
          </CSVLink>
          {selectedUsers && selectedUsers.length > 0 && (
            <>
              {type === "enabled" || type === "subscriptions"
                ? onDisableUsers && <Button color="red" onClick={onDisableUsers} mr={8}>Disable Selected Users</Button>
                : onEnableUsers && <Button onClick={onEnableUsers} mr={8}>Enable Selected Users</Button>}
            </>
          )}
        </div>
      )}

      <DataTable
        rowSelection={rowSelection}
        rowKey={(record) => record._id}
        loading={loading}
        scroll={{ x: 1200 }}
        dataSource={users?.data || []}
        pagination={{
          onChange: (page, size) => {
            if (setCurrent) setCurrent(page); if (setLimit) setLimit(size);
            if (getAllUserData) getAllUserData(page, size);
          },
          onShowSizeChange: (cur, size) => {
            if (setCurrent) setCurrent(1); if (setLimit) setLimit(size);
            if (getAllUserData) getAllUserData(1, size);
          },
          current: current || 1, pageSize: limit || 20,
          total: users?.metadata?.totalData || 0,
          pageSizeOptions: [20, 100, 500, 1000], showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        }}
        columns={[
          getActionColumn(),
          {
            title: "Image", dataIndex: "image", key: "image",
            render: (text) => <Image src={text} w={50} h={50} radius="xl" />,
          },
          { title: "Name", dataIndex: "name", key: "name" },
          { title: "Email", dataIndex: "email", key: "email" },
          { title: "Phone Number", dataIndex: "phoneNumber", key: "phoneNumber" },
          {
            title: "Creation Date", dataIndex: "createdAt", key: "createdAt",
            render: (text) => new Date(text).toLocaleDateString(),
          },
        ]}
      />

      <Modal opened={modal3} onClose={() => setModal3(false)} title="Notification" size="lg">
        <DataTable
          rowKey={(record) => record._id}
          dataSource={userNotifications || []}
          columns={[
            { title: "Image", dataIndex: "image", key: "image", render: (text) => <Image src={text} w={50} h={50} radius="xl" /> },
            { title: "Title", dataIndex: "title", key: "title" },
            { title: "Description", dataIndex: "description", key: "description" },
            { title: "Created At", dataIndex: "createdAt", key: "createdAt", render: (text) => new Date(text).toLocaleDateString() },
            { title: "Read", dataIndex: "read", key: "read", render: (text) => (text ? "Read" : "Unread") },
          ]}
        />
      </Modal>
    </div>
  );
};

UsersTable.propTypes = {
  users: PropTypes.object, loading: PropTypes.bool, getAllUserData: PropTypes.func,
  type: PropTypes.oneOf(["enabled", "disabled", "subscriptions"]),
  selectedUsers: PropTypes.array, setSelectedUsers: PropTypes.func,
  onEnableUsers: PropTypes.func, onDisableUsers: PropTypes.func,
  current: PropTypes.number, setCurrent: PropTypes.func,
  limit: PropTypes.number, setLimit: PropTypes.func, currentPage: PropTypes.number,
};

const UpdatePhotoButton = ({ record, getAllUserData, current, limit, showSuccess, showError }) => {
  const [loading3, setLoading3] = useState(false);
  return (
    <FileButton onChange={async (file) => {
      if (!file) return;
      setLoading3(true);
      const formData = new FormData();
      formData.append("image", file);
      const [success, response] = await updateUserImage(record._id, formData);
      if (success) { showSuccess("User image updated successfully"); if (getAllUserData && current && limit) getAllUserData(current, limit); }
      else { showError(response); }
      setLoading3(false);
    }} accept="image/*">
      {(props) => <Button variant="default" size="xs" leftSection={<Upload size={14} />} loading={loading3} {...props} fullWidth>Update User Photo</Button>}
    </FileButton>
  );
};

const SubscriptionLink = ({ item, showSuccess, showError, subscription }) => {
  const [currentSub, setCurrentSub] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [paymentLink, setPaymentLink] = useState("");
  const [linkModal, setLinkModal] = useState(false);

  const decodeBase64 = (base64String) => { try { return atob(base64String); } catch { showError("Failed to decode payment link"); return ""; } };

  const handlePhoneNumberChange = (e) => {
    const value = e.target.value;
    if (value.startsWith("+255")) setPhoneNumber(value);
    else if (value.length === 0) setPhoneNumber("+255");
    else { const cleaned = value.replace(/^\+255/, "").replace(/[^0-9]/g, ""); setPhoneNumber("+255" + cleaned); }
  };

  const generateLink = async () => {
    if (!currentSub) return showError("Please select subscription");
    if (!phoneNumber || phoneNumber.length <= 4) return showError("Please enter a valid phone number");
    setLoading(true);
    const [success, response] = await generatePaymentLink({ userId: item._id, phoneNumber, subscriptionId: currentSub });
    if (success && response?.data && response.data[0]?.payment_gateway_url) {
      setPaymentLink(decodeBase64(response.data[0].payment_gateway_url));
      setModal(false); setLinkModal(true); setCurrentSub(""); setPhoneNumber("");
    } else { showError(response?.message || "Failed to generate payment link"); }
    setLoading(false);
  };

  const copyToClipboard = () => { navigator.clipboard.writeText(paymentLink).then(() => showSuccess("Payment link copied to clipboard!")); };

  return (
    <>
      <Button onClick={() => setModal(true)} fullWidth size="xs">Subscription Link</Button>
      <Modal opened={modal} onClose={() => { setModal(false); setCurrentSub(""); setPhoneNumber(""); }} title="Generate Subscription Payment Link">
        <div style={{ marginBottom: 16 }}>
          <Select label="Select Subscription" value={currentSub} onChange={setCurrentSub} placeholder="Select Subscription"
            data={(subscription || []).map((sub) => ({ label: sub.title, value: sub._id }))} mb="md" />
          <TextInput label="Phone Number" value={phoneNumber} onChange={handlePhoneNumberChange} placeholder="+255XXXXXXXXX"
            onFocus={() => { if (!phoneNumber) setPhoneNumber("+255"); }} />
        </div>
        <Button onClick={generateLink} loading={loading}>Generate Link</Button>
      </Modal>
      <Modal opened={linkModal} onClose={() => { setLinkModal(false); setPaymentLink(""); }} title="Payment Link">
        <p style={{ marginBottom: 16, color: "#ff4d4f" }}>This link is only active for 5 minutes</p>
        <div className="flex gap-2 mb-4">
          <TextInput value={paymentLink} readOnly style={{ flex: 1 }} />
          <Button leftSection={<Copy size={14} />} onClick={copyToClipboard}>Copy</Button>
        </div>
        {paymentLink && (
          <div style={{ marginTop: 16 }}>
            <a href={paymentLink} target="_blank" rel="noopener noreferrer" style={{ wordBreak: "break-all" }}>{paymentLink}</a>
          </div>
        )}
      </Modal>
    </>
  );
};

export default UsersTable;
